import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PaginationControls,
  ResponsiveTable,
  Text,
} from '@shared/ui';

export function TableShowcase() {
  const { t } = useTranslation();

  const totalItems = 100;
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [density, setDensity] = useState<'default' | 'compact'>('default');

  const rows = [
    {
      name: t('uiLibrary.dataDisplay.table.rows.0.name'),
      role: t('uiLibrary.dataDisplay.table.rows.0.role'),
      status: t('uiLibrary.dataDisplay.table.rows.0.status'),
    },
    {
      name: t('uiLibrary.dataDisplay.table.rows.1.name'),
      role: t('uiLibrary.dataDisplay.table.rows.1.role'),
      status: t('uiLibrary.dataDisplay.table.rows.1.status'),
    },
    {
      name: t('uiLibrary.dataDisplay.table.rows.2.name'),
      role: t('uiLibrary.dataDisplay.table.rows.2.role'),
      status: t('uiLibrary.dataDisplay.table.rows.2.status'),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('uiLibrary.dataDisplay.table.title')}</CardTitle>
        <CardDescription>{t('uiLibrary.dataDisplay.table.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="density">
          {(['default', 'compact'] as const).map((value) => (
            <Button
              key={value}
              variant={density === value ? 'default' : 'outline'}
              size="sm"
              density={density}
              aria-pressed={density === value}
              onClick={() => {
                setDensity(value);
              }}
            >
              <code>{`density="${value}"`}</code>
            </Button>
          ))}
        </div>
        <PaginationControls
          className="mb-4"
          totalItems={totalItems}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          labels={{
            showing: (from, to, total) =>
              t('uiLibrary.navigation.pagination.showing', {
                from,
                to,
                total,
              }),
            page: t('uiLibrary.navigation.pagination.pageLabel'),
            of: (totalPages) => t('uiLibrary.navigation.pagination.ofLabel', { total: totalPages }),
          }}
        />
        <ResponsiveTable
          density={density}
          rows={rows}
          getRowKey={(row) => row.name}
          tableClassName="border border-border"
          tableHeaderClassName="bg-[hsl(var(--surface-subtle))]"
          caption={t('uiLibrary.dataDisplay.table.caption')}
          columns={[
            {
              key: 'name',
              header: t('uiLibrary.dataDisplay.table.headers.name'),
              cell: (row) => <span className="font-medium">{row.name}</span>,
            },
            {
              key: 'role',
              header: t('uiLibrary.dataDisplay.table.headers.role'),
              cell: (row) => row.role,
            },
            {
              key: 'status',
              header: t('uiLibrary.dataDisplay.table.headers.status'),
              cell: (row) => row.status,
            },
          ]}
          renderMobileCard={(row) => (
            <Card>
              <CardContent className="space-y-3 p-4">
                <div>
                  <Text size="xs" color="muted">
                    {t('uiLibrary.dataDisplay.table.headers.name')}
                  </Text>
                  <Text className="font-medium text-card-foreground">{row.name}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    {t('uiLibrary.dataDisplay.table.headers.role')}
                  </Text>
                  <Text size="sm">{row.role}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    {t('uiLibrary.dataDisplay.table.headers.status')}
                  </Text>
                  <Text size="sm">{row.status}</Text>
                </div>
              </CardContent>
            </Card>
          )}
        />
        <div className="mt-4">
          <ResponsiveTable
            rows={[]}
            columns={[]}
            emptyState={{
              title: t('uiLibrary.dataDisplay.table.emptyTitle', 'No table records'),
              description: t(
                'uiLibrary.dataDisplay.table.emptyDescription',
                'Try changing the search or filters.'
              ),
            }}
            renderMobileCard={() => null}
          />
        </div>
        <PaginationControls
          className="mt-4"
          totalItems={totalItems}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          labels={{
            showing: (from, to, total) =>
              t('uiLibrary.navigation.pagination.showing', {
                from,
                to,
                total,
              }),
            page: t('uiLibrary.navigation.pagination.pageLabel'),
            of: (totalPages) => t('uiLibrary.navigation.pagination.ofLabel', { total: totalPages }),
          }}
        />
      </CardContent>
    </Card>
  );
}
